import { useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { PhotoUploadDropzone } from '../components/atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AiLimitErrorMessage, AiLimitNotice } from '../components/aiUsage/AiLimitNotice';
import { GalleryGrid } from '../components/gallery/GalleryGrid';
import { CoralCheckFindings } from '../components/coralCheck/CoralCheckFindings';
import { useGalleryImages } from '../hooks/useGalleryImages';
import { useInvalidateGallery } from '../hooks/useGalleryOrganisation';
import { useAiUsage, invalidateAiUsage } from '../hooks/useAiUsage';
import { getAiLimitErrorKind } from '../services/aiUsageService';
import { analyzeImage, fetchImageInsights, uploadGalleryImages } from '../services/galleryService';
import { useTranslation } from '../i18n/useTranslation';
import './CoralCheckPage.css';

/** Same cache key as the gallery's details panel, so both show the same findings. */
const insightsKey = (imageId: string) => ['imageInsights', imageId] as const;

/** The photo being checked: from the gallery list, or just uploaded (local preview). */
interface CheckedPhoto {
  id: string;
  src: string | null;
  hasAiAnalysis: boolean;
}

/**
 * Korallen-Check / coral check: the gallery's per-photo AI analysis as its own
 * entry point. Step 1 picks a photo (upload a new one or choose one from the
 * gallery), step 2 runs the analysis and shows the findings with "Health &
 * stress" first. Uses the existing image endpoints (upload, analyze, insights);
 * the analysis only runs on request and uses AI credits.
 */
export function CoralCheckPage() {
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const invalidateGallery = useInvalidateGallery(aquariumId);
  const { exceeded, usage } = useAiUsage();
  const hintId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const [photo, setPhoto] = useState<CheckedPhoto | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const gallery = useGalleryImages({ aquariumId, limit: 12 });

  // Revoke the object URL of an uploaded file once it is no longer shown.
  useEffect(() => {
    if (!localPreview) return undefined;
    return () => URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const insights = useQuery({
    queryKey: insightsKey(photo?.id ?? ''),
    queryFn: () => fetchImageInsights(photo?.id ?? ''),
    enabled: photo !== null && photo.hasAiAnalysis,
  });

  const analyse = useMutation({
    mutationFn: (imageId: string) => analyzeImage(imageId, i18n.language || 'en'),
    onSuccess: (result, imageId) => {
      queryClient.setQueryData(insightsKey(imageId), result);
      setAnnouncement(t('gallery.details.ai.done', { n: result.length }));
      invalidateGallery();
      resultRef.current?.focus();
    },
    onSettled: () => invalidateAiUsage(queryClient),
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadGalleryImages(aquariumId, [file], setUploadPercent),
    onMutate: (file) => {
      setUploadPercent(0);
      setAnnouncement(t('coralCheck.pick.uploadingStart'));
      return { preview: URL.createObjectURL(file) };
    },
    onSuccess: (images, _file, context) => {
      const created = images[0];
      if (!created) {
        if (context?.preview) URL.revokeObjectURL(context.preview);
        return;
      }
      analyse.reset();
      setLocalPreview(context?.preview ?? null);
      setPhoto({ id: created.id, src: context?.preview ?? null, hasAiAnalysis: false });
      setAnnouncement(t('coralCheck.pick.uploaded'));
      invalidateGallery();
    },
    onError: (_err, _file, context) => {
      if (context?.preview) URL.revokeObjectURL(context.preview);
    },
  });

  useEffect(() => {
    if (analyse.isError) errorRef.current?.focus();
  }, [analyse.isError]);

  const selectFromGallery = (imageId: string): void => {
    analyse.reset();
    if (photo?.id === imageId) {
      setPhoto(null);
      return;
    }
    const image = gallery.images.find((img) => img.id === imageId);
    if (!image) return;
    setLocalPreview(null);
    setPhoto({ id: image.id, src: image.displayUrl ?? image.thumbnailUrl ?? null, hasAiAnalysis: image.hasAiAnalysis });
    setAnnouncement(t('coralCheck.pick.selected'));
  };

  const findings = (photo && analyse.data && analyse.variables === photo.id ? analyse.data : null) ?? insights.data ?? [];
  const analysed = photo !== null && (photo.hasAiAnalysis || (analyse.isSuccess && analyse.variables === photo.id));
  const limitError = analyse.isError && getAiLimitErrorKind(analyse.error) !== null;
  const showLoading = photo !== null && photo.hasAiAnalysis && insights.isLoading && !analyse.data;

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" className="coral-check">
          <PageHeader title={t('coralCheck.title')} description={t('coralCheck.description')} />

          <Section title={t('coralCheck.pick.title')} description={t('coralCheck.pick.description')}>
            <Stack gap="4">
              <PhotoUploadDropzone
                onChange={(file) => upload.mutate(file)}
                disabled={upload.isPending || analyse.isPending}
                ariaLabel={t('coralCheck.pick.uploadLabel')}
              />
              {upload.isPending && (
                <Text variant="muted">{t('coralCheck.pick.uploading', { percent: uploadPercent })}</Text>
              )}
              {upload.isError && <Alert compact>{t('coralCheck.pick.uploadError')}</Alert>}

              <Heading level={3} className="coral-check__subheading">
                {t('coralCheck.pick.fromGallery')}
              </Heading>
              {gallery.error ? (
                <LoadErrorState
                  compact
                  titleLevel={3}
                  title={t('coralCheck.pick.galleryLoadError')}
                  onRetry={() => gallery.refetch?.()}
                  retryLabel={t('common.retry')}
                />
              ) : (
                <GalleryGrid
                  images={gallery.images}
                  isLoading={gallery.isLoading || gallery.isFetchingNextPage}
                  hasNextPage={gallery.hasNextPage}
                  onLoadMore={gallery.fetchNextPage}
                  onImageClick={() => undefined}
                  selectionMode
                  selectedIds={new Set(photo ? [photo.id] : [])}
                  onToggleSelect={selectFromGallery}
                />
              )}
            </Stack>
          </Section>

          <Section title={t('coralCheck.result.title')}>
            {photo === null ? (
              <Text variant="muted">{t('coralCheck.result.pickFirst')}</Text>
            ) : (
              <Card padding="lg" className="coral-check__result">
                <div className="coral-check__photo">
                  {photo.src ? (
                    <img className="coral-check__image" src={photo.src} alt={t('coralCheck.result.previewAlt')} />
                  ) : (
                    <Text variant="muted">{t('coralCheck.result.noPreview')}</Text>
                  )}
                </div>
                <Stack gap="3" className="coral-check__details">
                  <Text variant="caption" id={hintId}>
                    {t('gallery.details.ai.hint')}
                  </Text>
                  {exceeded && !analyse.isError ? (
                    <AiLimitNotice kind="exceeded" resetsAt={usage?.resetsAt} live="off" />
                  ) : null}
                  <div className="coral-check__actions">
                    <Button
                      variant="primary"
                      onClick={() => analyse.mutate(photo.id)}
                      isLoading={analyse.isPending}
                      disabled={analyse.isPending || upload.isPending || exceeded}
                      aria-describedby={hintId}
                      data-testid="coral-check-run"
                    >
                      {analyse.isPending
                        ? t('coralCheck.result.running')
                        : analysed
                          ? t('coralCheck.result.rerun')
                          : t('coralCheck.result.run')}
                    </Button>
                    <Link className="coral-check__link" to={`/aquariums/${encodeURIComponent(aquariumId)}/gallery`}>
                      {t('coralCheck.result.openGallery')}
                    </Link>
                  </div>
                  {analyse.isError &&
                    (limitError ? (
                      <AiLimitErrorMessage ref={errorRef} error={analyse.error} tabIndex={-1} />
                    ) : (
                      <Alert ref={errorRef} tabIndex={-1} compact>
                        {t('gallery.details.ai.error')}
                      </Alert>
                    ))}

                  <div ref={resultRef} tabIndex={-1} role="group" className="coral-check__findings" aria-label={t('coralCheck.result.findingsLabel')}>
                    {insights.isError && !analyse.data ? (
                      <LoadErrorState
                        compact
                        titleLevel={3}
                        title={t('gallery.details.ai.loadError')}
                        onRetry={() => void insights.refetch()}
                        retryLabel={t('common.retry')}
                      />
                    ) : showLoading ? (
                      <Text variant="muted">{t('common.loading')}</Text>
                    ) : findings.length === 0 ? (
                      <Text variant="muted">
                        {analysed ? t('gallery.details.ai.noFindings') : t('coralCheck.result.notYet')}
                      </Text>
                    ) : (
                      <Stack gap="3">
                        <CoralCheckFindings insights={findings} />
                        <Text variant="caption">{t('gallery.details.ai.disclaimer')}</Text>
                      </Stack>
                    )}
                  </div>
                </Stack>
              </Card>
            )}
          </Section>
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        </PageContent>
      }
    />
  );
}

export default CoralCheckPage;
